/**
 * The one rule for every analytics consumer: events leave the DEPLOYED product and nothing else.
 *
 * HOST-BASED, deliberately not `import.meta.env.DEV`. `npm run build && npm start` serves a
 * PRODUCTION build from localhost — there DEV is false, so a DEV check lets those events through and
 * they arrive tagged `environment: 'prod'`, quietly corrupting the real funnel. That is worse than a
 * dev event, because nothing marks it as fake. The hostname is the only thing that tells the real
 * deployment apart from a copy of it running on someone's machine.
 *
 * It also stops relying on discipline with `.env`: the keys can sit in a local `.env` (they have to,
 * to test that the wiring works at all) without a single event escaping.
 *
 * Deliberate debugging, no rebuild needed — in the browser console:
 *   localStorage.setItem('intch.analytics.force', '1')
 * Such sends are tagged `environment: 'dev'` by analyticsEnvironment(), so they stay filterable.
 * Force opens Amplitude only — the ad platforms have no such tag, see marketingAllowed().
 */

/** Every host that is the real product. A staging host would be added here, never inferred. */
const PROD_HOSTS = ['sales.intch.org']

const FORCE_KEY = 'intch.analytics.force'

const onProdHost = () =>
  typeof window !== 'undefined' && PROD_HOSTS.includes(window.location.hostname)

const forced = () => {
  try {
    return typeof window !== 'undefined' && window.localStorage.getItem(FORCE_KEY) === '1'
  } catch {
    // A blocked storage accessor (private mode, embedded view) must never turn INTO a send.
    return false
  }
}

/** Whether this page may talk to an analytics vendor at all. */
export const analyticsAllowed = (): boolean => onProdHost() || forced()

/**
 * THE AD PLATFORMS (FB, LinkedIn, GA) — PROD HOST ONLY; the force switch does not open them
 * (2026-09-28, before the funnel split test).
 *
 * Force exists for Amplitude, where every event carries `environment` and a debugging session is
 * filtered out afterwards. A pixel or GA hit has no such tag: a forced PageView or Lead from a
 * developer's machine lands in campaign optimisation and in the custom conversions as a real one,
 * and nothing can separate it later. Check the ad wiring on sales.intch.org itself (Pixel Helper;
 * `?v=<letter>` is an override and never joins the test) or with a build opened under the prod host —
 * never with a localStorage flag.
 */
export const marketingAllowed = (): boolean => onProdHost()

/**
 * What to stamp events with. Derived from the host rather than from the build, so a forced send from
 * a machine reads as 'dev' even when the bundle was built in production mode.
 */
export const analyticsEnvironment = (): 'prod' | 'dev' => (onProdHost() ? 'prod' : 'dev')
